<template>
  <div class="crash-admin-config">
    <!-- Header -->
    <div class="config-header">
      <h2 class="config-title">{{ $t('admin.config.crash.title') }}</h2>
      <p class="config-subtitle">{{ $t('admin.config.crash.subtitle') }}</p>
    </div>

    <div v-if="loading" class="loading-state">{{ $t('admin.config.loading') }}</div>
    <div v-else-if="loadError" class="error-banner">{{ loadError }}</div>

    <form v-else class="settings-form" @submit.prevent="saveConfig">

      <!-- House Edge -->
      <section class="settings-section">
        <h3 class="section-title">{{ $t('admin.config.section_house_edge') }}</h3>
        <div class="form-row">
          <label class="form-label">
            {{ $t('admin.config.label_house_edge') }}
            <input
              v-model.number="form.house_edge_pct"
              type="number"
              step="0.5"
              min="1"
              max="10"
              class="form-input"
              required
            />
            <span class="form-hint">{{ $t('admin.config.hint_house_edge') }}</span>
          </label>
          <div class="rtp-display">
            <span class="rtp-label">{{ $t('admin.config.label_rtp') }}</span>
            <span class="rtp-value">{{ rtpDisplay }}%</span>
          </div>
        </div>
      </section>

      <!-- Game Loop -->
      <section class="settings-section">
        <h3 class="section-title">{{ $t('admin.config.crash.section_game_loop') }}</h3>

        <div class="form-row">
          <label class="form-label">
            {{ $t('admin.config.crash.label_betting_phase') }}
            <input
              v-model.number="form.betting_phase_secs"
              type="number"
              step="1"
              min="5"
              max="15"
              class="form-input"
              required
            />
            <span class="form-hint">5–15 seconds (default 10). Restart RunCrashGameLoop after changing.</span>
          </label>
        </div>

        <div class="form-row">
          <label class="form-label">
            {{ $t('admin.config.crash.label_growth_rate') }}
            <input
              v-model="form.growth_rate"
              type="number"
              step="0.000001"
              min="0.000001"
              class="form-input"
              required
            />
            <span class="form-hint">Multiplier growth rate per millisecond (e.g. 0.00006). Restart RunCrashGameLoop after changing.</span>
          </label>
        </div>

        <div class="note-banner">
          Changing <strong>betting_phase_secs</strong> or <strong>growth_rate</strong> requires restarting the <code>RunCrashGameLoop</code> artisan command.
        </div>
      </section>

      <!-- Bet Limits -->
      <section class="settings-section">
        <h3 class="section-title">{{ $t('admin.config.section_bet_limits') }}</h3>
        <div class="form-row">
          <label class="form-label">
            {{ $t('admin.config.label_min_bet') }}
            <input
              v-model="form.min_bet"
              type="number"
              step="0.01"
              min="0.01"
              class="form-input"
              required
            />
          </label>
          <label class="form-label">
            {{ $t('admin.config.label_max_bet') }}
            <input
              v-model="form.max_bet"
              type="number"
              step="0.01"
              min="0.01"
              class="form-input"
              required
            />
            <span class="form-hint">{{ $t('admin.config.hint_max_bet_gt_min') }}</span>
          </label>
        </div>
        <div class="form-row">
          <label class="form-label">
            {{ $t('admin.config.label_max_payout_simple') }}
            <input
              v-model="form.max_payout"
              type="number"
              step="0.01"
              min="0"
              class="form-input"
              required
            />
            <span class="form-hint">{{ $t('admin.config.hint_no_cap') }}</span>
          </label>
        </div>
      </section>

      <!-- Advanced -->
      <section class="settings-section">
        <h3 class="section-title">{{ $t('admin.config.crash.section_advanced') }}</h3>

        <div class="form-row">
          <label class="form-label">
            {{ $t('admin.config.crash.label_history_length') }}
            <input
              v-model.number="form.history_length"
              type="number"
              step="1"
              min="1"
              class="form-input"
              required
            />
            <span class="form-hint">Number of past rounds shown in the history strip (default 100).</span>
          </label>
        </div>

        <div class="form-row">
          <label class="form-label">
            {{ $t('admin.config.crash.label_max_players') }}
            <input
              v-model.number="form.max_players_per_round"
              type="number"
              step="1"
              min="0"
              class="form-input"
              required
            />
            <span class="form-hint">0 = unlimited.</span>
          </label>
        </div>
      </section>

      <div v-if="saveError" class="error-banner">{{ saveError }}</div>
      <div v-if="saveSuccess" class="success-banner">{{ saveSuccess }}</div>

      <div class="form-actions">
        <button type="submit" class="btn btn-primary" :disabled="saving">
          {{ saving ? $t('admin.config.saving') : $t('admin.config.save_config') }}
        </button>
        <button type="button" class="btn btn-secondary" :disabled="saving" @click="resetForm">
          {{ $t('admin.config.reset') }}
        </button>
        <span v-if="sourceLabel" class="source-badge" :class="sourceClass">
          {{ sourceLabel }}
        </span>
      </div>
    </form>
  </div>
</template>

<script setup lang="ts">
import { ref, reactive, computed, onMounted } from 'vue'
import { useI18n } from 'vue-i18n'
import apiClient from '@/api/client'
import { useToastStore } from '@/stores/toast'

interface CrashConfigResponse {
  source: 'database' | 'default'
  house_edge_pct: number
  betting_phase_secs: number
  min_bet: string
  max_bet: string
  max_payout: string
  growth_rate: number
  history_length: number
  max_players_per_round: number
  rtp: number
}

const { t } = useI18n()
const toast = useToastStore()

const loading = ref(true)
const saving = ref(false)
const loadError = ref('')
const saveError = ref('')
const saveSuccess = ref('')

const source = ref<'database' | 'default'>('default')

const form = reactive({
  house_edge_pct: 1.0,
  betting_phase_secs: 10,
  min_bet: '0.01',
  max_bet: '10000.00',
  max_payout: '0.00',
  growth_rate: 0.00006,
  history_length: 100,
  max_players_per_round: 0,
})

let originalForm: typeof form | null = null

const rtpDisplay = computed(() => (100 - form.house_edge_pct).toFixed(2))

const sourceLabel = computed(() => (source.value === 'database' ? t('admin.config.saved_to_db') : t('admin.config.using_defaults')))
const sourceClass = computed(() => (source.value === 'database' ? 'source-db' : 'source-default'))

async function loadConfig() {
  loading.value = true
  loadError.value = ''
  try {
    const res = await apiClient.get<CrashConfigResponse>('/admin/api/games/crash/config')
    populateForm(res.data)
  } catch {
    loadError.value = t('admin.config.crash.error_load')
  } finally {
    loading.value = false
  }
}

function populateForm(data: CrashConfigResponse) {
  source.value = data.source
  form.house_edge_pct = data.house_edge_pct
  form.betting_phase_secs = data.betting_phase_secs
  form.min_bet = data.min_bet
  form.max_bet = data.max_bet
  form.max_payout = data.max_payout
  form.growth_rate = data.growth_rate
  form.history_length = data.history_length
  form.max_players_per_round = data.max_players_per_round
  originalForm = { ...form }
}

function resetForm() {
  if (!originalForm) return
  Object.assign(form, originalForm)
  saveError.value = ''
  saveSuccess.value = ''
}

async function saveConfig() {
  saveError.value = ''
  saveSuccess.value = ''
  saving.value = true

  try {
    const res = await apiClient.post<CrashConfigResponse>('/admin/api/games/crash/config', {
      house_edge_pct: form.house_edge_pct,
      betting_phase_secs: form.betting_phase_secs,
      min_bet: form.min_bet,
      max_bet: form.max_bet,
      max_payout: form.max_payout,
      growth_rate: form.growth_rate,
      history_length: form.history_length,
      max_players_per_round: form.max_players_per_round,
    })
    populateForm(res.data)
    saveSuccess.value = t('admin.config.crash.save_success')
    toast.success(t('admin.config.crash.save_success'))
  } catch (err: unknown) {
    const e = err as { response?: { data?: { message?: string } } }
    saveError.value = e.response?.data?.message ?? t('admin.config.crash.error_save')
    toast.error(saveError.value)
  } finally {
    saving.value = false
  }
}

onMounted(loadConfig)
</script>

<style scoped>
.crash-admin-config {
  padding: 0;
}

.config-header {
  margin-bottom: 24px;
}

.config-title {
  font-size: 1.375rem;
  font-weight: 700;
  color: var(--text-primary, #fff);
  margin: 0 0 6px;
}

.config-subtitle {
  color: var(--text-secondary, #b1bad3);
  font-size: 0.875rem;
  margin: 0;
}

/* Loading / error / success */
.loading-state {
  color: var(--text-secondary, #b1bad3);
  padding: 32px;
  text-align: center;
}

.error-banner {
  background: rgba(239, 68, 68, 0.12);
  border: 1px solid rgba(239, 68, 68, 0.4);
  border-radius: 8px;
  color: var(--color-danger-light, #f87171);
  padding: 12px 16px;
  font-size: 0.875rem;
  margin-bottom: 16px;
}

.success-banner {
  background: rgba(26, 158, 110, 0.12);
  border: 1px solid rgba(26, 158, 110, 0.4);
  border-radius: 8px;
  color: var(--color-success-light, #34d399);
  padding: 12px 16px;
  font-size: 0.875rem;
  margin-bottom: 16px;
}

.note-banner {
  background: rgba(251, 191, 36, 0.08);
  border: 1px solid rgba(251, 191, 36, 0.3);
  border-radius: 8px;
  color: var(--color-warning-light, #fbbf24);
  padding: 10px 14px;
  font-size: 0.8rem;
  margin-top: 14px;
}

.note-banner strong,
.note-banner code {
  font-weight: 600;
}

.note-banner code {
  background: rgba(251, 191, 36, 0.15);
  border-radius: 4px;
  padding: 1px 5px;
  font-family: monospace;
}

/* Form */
.settings-form {
  max-width: 680px;
}

.settings-section {
  background: var(--bg-secondary, #213743);
  border: 1px solid var(--border, #2d4a5a);
  border-radius: 10px;
  padding: 20px;
  margin-bottom: 20px;
}

.section-title {
  font-size: 0.95rem;
  font-weight: 600;
  color: var(--text-primary, #fff);
  margin: 0 0 16px;
}

.form-row {
  display: flex;
  gap: 16px;
  flex-wrap: wrap;
  margin-bottom: 14px;
  align-items: flex-start;
}

.form-row:last-child {
  margin-bottom: 0;
}

.form-label {
  display: flex;
  flex-direction: column;
  gap: 6px;
  font-size: 0.8rem;
  color: var(--text-secondary, #b1bad3);
  flex: 1;
  min-width: 180px;
}

.form-input {
  padding: 8px 12px;
  background: var(--bg-primary, #0f212e);
  border: 1px solid var(--border, #2d4a5a);
  border-radius: 6px;
  color: var(--text-primary, #fff);
  font-size: 0.875rem;
}

.form-hint {
  font-size: 0.72rem;
  color: var(--text-secondary, #b1bad3);
  opacity: 0.7;
}

/* RTP display inline with house edge */
.rtp-display {
  display: flex;
  flex-direction: column;
  gap: 6px;
  align-items: center;
  justify-content: center;
  min-width: 80px;
  padding-top: 18px;
}

.rtp-label {
  font-size: 0.75rem;
  color: var(--text-secondary, #b1bad3);
}

.rtp-value {
  font-size: 1.25rem;
  font-weight: 700;
  color: var(--color-success-light, #34d399);
}

/* Actions row */
.form-actions {
  display: flex;
  align-items: center;
  gap: 14px;
  margin-top: 8px;
}

/* Source badge */
.source-badge {
  font-size: 0.75rem;
  font-weight: 600;
  padding: 4px 10px;
  border-radius: 6px;
}

.source-db {
  background: rgba(59, 130, 246, 0.2);
  color: var(--color-info-light, #60a5fa);
}

.source-default {
  background: rgba(107, 114, 128, 0.2);
  color: var(--color-muted, #9ca3af);
}

/* Buttons */
.btn {
  padding: 9px 20px;
  border-radius: 7px;
  font-size: 0.875rem;
  font-weight: 600;
  cursor: pointer;
  border: none;
  transition: opacity 0.15s;
}

.btn:disabled {
  opacity: 0.55;
  cursor: default;
}

.btn-primary {
  background: var(--accent, #1a9e6e);
  color: var(--color-on-dark, #fff);
}

.btn-secondary {
  background: var(--bg-primary, #0f212e);
  border: 1px solid var(--border, #2d4a5a);
  color: var(--text-secondary, #b1bad3);
}
</style>
